import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink } from "lucide-react";

export default function AdminPaymentGateway() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, mode: "TEST", client_id: "", client_secret: "", webhook_secret: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/payment-gateway/cashfree");
    setCfg(data);
    setForm((f) => ({ ...f, enabled: data.enabled, mode: data.mode }));
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        enabled: form.enabled,
        mode: form.mode,
        client_id: form.client_id || null,
        client_secret: form.client_secret || null,
        webhook_secret: form.webhook_secret || null,
      };
      await api.post("/admin/payment-gateway/cashfree", payload);
      toast.success("Cashfree config saved");
      setForm({ ...form, client_id: "", client_secret: "", webhook_secret: "" });
      load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Payment"
        title="Cashfree configuration"
        description="Keys are encrypted at rest using the platform master key. Switch between TEST and LIVE without redeploying."
        testid="admin-gateway-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-3xl space-y-6">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Enabled" value={cfg?.enabled ? "Yes" : "No"} highlight={cfg?.enabled} testid="cf-stat-enabled" />
          <Stat label="Mode" value={cfg?.mode || "—"} testid="cf-stat-mode" />
          <Stat label="Keys configured" value={cfg?.has_client_id ? "Yes" : "No"} highlight={cfg?.has_client_id} testid="cf-stat-keys" />
        </div>

        <form onSubmit={save} className="border border-border p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="label-eyebrow">Enable gateway</Label>
              <p className="text-xs text-muted-foreground">When disabled, all checkouts return "not configured".</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="cf-enable-switch" />
          </div>

          <div>
            <Label className="label-eyebrow mb-2 inline-block">Mode</Label>
            <RadioGroup value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })} className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="TEST" data-testid="cf-mode-test" />
                <span className="text-sm">Test (sandbox.cashfree.com)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="LIVE" data-testid="cf-mode-live" />
                <span className="text-sm">Live (api.cashfree.com)</span>
              </label>
            </RadioGroup>
          </div>

          <Field label="Cashfree App ID (Client ID)" placeholder={cfg?.has_client_id ? "•••• keep existing if blank" : "TEST123… or PROD123…"} value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v, enabled: form.enabled || (v.length > 3) })} testid="cf-client-id-input" />
          <Field label="Cashfree Secret Key" type="password" placeholder="•••• keep existing if blank" value={form.client_secret} onChange={(v) => setForm({ ...form, client_secret: v, enabled: form.enabled || (v.length > 3) })} testid="cf-client-secret-input" />
          <Field label="Webhook Secret" type="password" placeholder="•••• keep existing if blank" value={form.webhook_secret} onChange={(v) => setForm({ ...form, webhook_secret: v })} testid="cf-webhook-secret-input" />

          {/* Feb 2026 · Warning banner — surfaces the "keys present but
              disabled" state that historically caused every booking to
              silently skip payment. */}
          {cfg?.has_client_id && !cfg?.enabled && (
            <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-200">
              Keys are saved but the gateway is <b>disabled</b>. Toggle "Enable gateway" above and save — otherwise every checkout will fall back to "not configured".
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <div><strong>Webhook URL (configure in Cashfree dashboard):</strong></div>
            <code className="font-mono break-all text-foreground">{`${process.env.REACT_APP_BACKEND_URL}/api/v1/webhooks/cashfree`}</code>
            <a href="https://merchant.cashfree.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary mt-2"><ExternalLink className="w-3 h-3 mr-1" /> Open Cashfree dashboard</a>
          </div>

          <Button type="submit" disabled={busy} className="rounded-none" data-testid="cf-save-btn">
            <ShieldCheck className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save configuration"}
          </Button>
          {/* Feb 2026 · R268lll · Test-connection button — fires a
              real ₹1 sandbox order against the stored keys so admins
              can see 'authentication Failed' / 'Invalid key' etc
              directly instead of debugging via widget bookings. */}
          {cfg?.has_client_id && (
            <TestConnectionButton />
          )}
        </form>
      </div>
    </>
  );
}

function TestConnectionButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/payment-gateway/cashfree/test");
      setResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      setResult({ ok: false, message: apiErrorMessage(e, "Test failed") });
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 space-y-3" data-testid="cf-test-block">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow mb-1">Health check</div>
          <p className="text-xs text-muted-foreground">Fires a ₹1 sandbox order to verify the saved keys work.</p>
        </div>
        <Button type="button" variant="outline" onClick={run} disabled={busy}
                 className="rounded-none" data-testid="cf-test-btn">
          {busy ? "Testing…" : "Test connection"}
        </Button>
      </div>
      {result && (
        <div className={`text-xs p-3 border-l-4 ${result.ok ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200" : "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200"}`}
              data-testid="cf-test-result">
          <div className="font-bold mb-0.5">{result.ok ? "✓ Success" : "✗ Failed"}</div>
          <div>{result.message}</div>
          {result.detail && !result.ok && (
            <pre className="mt-2 text-[10px] overflow-x-auto whitespace-pre-wrap font-mono">{JSON.stringify(result.detail, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight, testid }) {
  return (
    <div className={`border p-5 ${highlight ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid={testid}>
      <div className="label-eyebrow opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}
